import { useQuery } from '@tanstack/react-query';
import { useId, type JSX, type ReactNode } from 'react';

import { getAuditLogs } from '@/features/audit/api/audit.graphql';
import { usePlatformPermissions } from '@/features/auth/hooks/usePlatformPermissions';
import { getTenantHealth, tenantHealthQueryKey } from '@/features/metrics/api/metrics.graphql';
import { HealthBadge } from '@/features/metrics/components/HealthBadge';
import { HEALTH_DEFINITION } from '@/features/metrics/health-labels';
import { formatMoney } from '@/lib/money';

import { tenantBillingQueryKey } from './TenantBillingTab';
import { tenantOnboardingQueryKey } from './TenantOnboardingPanel';
import styles from './TenantSummaryTab.module.scss';
import { getTenantSubscription, tenantSubscriptionQueryKey } from '../api/subscription.graphql';
import {
  getTenantBillingProfile,
  getTenantOnboarding,
  type TenantDetail,
} from '../api/tenants.graphql';
import { useModuleCatalog } from '../hooks/useModuleCatalog';
import { formatDateTime } from '../module-labels';
import { expiryAlertLabel, statusLabel, type TenantTab } from '../tenant-labels';

interface Props {
  tenant: TenantDetail;
  onGoToTab: (tab: TenantTab) => void;
}

interface OpenAlert {
  text: string;
  tab: TenantTab | null;
  action?: string;
}

function Block({ title, children, idPrefix, name }: { title: string; children: ReactNode; idPrefix: string; name: string }): JSX.Element {
  return (
    <section className={styles.block} aria-labelledby={`${idPrefix}-${name}`}>
      <h3 id={`${idPrefix}-${name}`} className={styles.blockTitle}>
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * OS-1724 — Resumen 360 del tenant: alertas abiertas, uso de los últimos 30 días, módulos,
 * suscripción y línea de tiempo. Reutiliza las consultas de cada pestaña (mismas claves de caché);
 * cada bloque aparece solo con la capacidad que lo permite (SOPORTE no ve facturación).
 */
export function TenantSummaryTab({ tenant, onGoToTab }: Props): JSX.Element {
  const idPrefix = useId();
  const { can } = usePlatformPermissions();
  const canBilling = can('platform.billing.read');
  const canReports = can('platform.reports.read');
  const canAudit = can('platform.audit.read');

  const onboarding = useQuery({
    queryKey: tenantOnboardingQueryKey(tenant.id),
    queryFn: () => getTenantOnboarding(tenant.id),
  });
  const health = useQuery({
    queryKey: tenantHealthQueryKey(tenant.id),
    queryFn: () => getTenantHealth(tenant.id),
    enabled: canReports,
  });
  const billing = useQuery({
    queryKey: tenantBillingQueryKey(tenant.id),
    queryFn: () => getTenantBillingProfile(tenant.id),
    enabled: canBilling,
  });
  const subscription = useQuery({
    queryKey: tenantSubscriptionQueryKey(tenant.id),
    queryFn: () => getTenantSubscription(tenant.id),
    enabled: canBilling,
  });
  const timeline = useQuery({
    queryKey: ['platform-audit-logs', 'tenant-summary', tenant.id],
    queryFn: () => getAuditLogs({ tenantId: tenant.id }, { take: 10 }),
    enabled: canAudit,
  });
  const catalog = useModuleCatalog();
  const moduleNames = new Map((catalog.data ?? []).map((module) => [module.key, module.name]));

  const alerts: OpenAlert[] = [];
  if (tenant.expiryAlert && tenant.expiryAlert.level !== 'NONE') {
    alerts.push({ text: `Piloto: ${expiryAlertLabel(tenant.expiryAlert)}.`, tab: null, action: 'Decide arriba: extender, convertir o cancelar.' });
  }
  if (tenant.planStatus === 'past_due') {
    alerts.push({ text: 'En mora: mantiene el acceso hasta que se regularice o se suspenda.', tab: canBilling ? 'billing' : null });
  }
  if (tenant.planStatus === 'suspended') {
    alerts.push({ text: 'Suspendido: los usuarios del tenant no pueden entrar.', tab: null });
  }
  if (onboarding.data?.stalled) {
    alerts.push({ text: `Onboarding estancado (${onboarding.data.percent} %).`, tab: 'general' });
  }
  if (billing.data && !billing.data.complete && (tenant.planStatus === 'pilot' || tenant.planStatus === 'trial')) {
    alerts.push({ text: 'Perfil de facturación incompleto: no se podrá convertir a pago.', tab: 'billing' });
  }
  if (health.data?.health.usageDrop) {
    alerts.push({ text: 'Caída de uso: el DAU de 7 días está bajo la mitad del de 30.', tab: null });
  }

  return (
    <div className={styles.summary}>
      <Block title="Alertas abiertas" idPrefix={idPrefix} name="alerts">
        {alerts.length === 0 ? (
          <p className={styles.muted}>Sin alertas abiertas.</p>
        ) : (
          <ul className={styles.list}>
            {alerts.map((alert) => (
              <li key={alert.text} className={styles.alert}>
                {alert.text} {alert.action ? <span className={styles.muted}>{alert.action}</span> : null}
                {alert.tab ? (
                  <button type="button" className={styles.linkBtn} onClick={() => onGoToTab(alert.tab as TenantTab)}>
                    Ir a resolver
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Block>

      <Block title="Uso de los últimos 30 días" idPrefix={idPrefix} name="usage">
        {!canReports ? (
          <p className={styles.muted}>Tu rol no ve métricas de uso.</p>
        ) : health.isLoading ? (
          <p className={styles.muted} role="status">Cargando uso…</p>
        ) : health.isError ? (
          <p className={styles.error} role="alert">
            No se pudo cargar el uso: {health.error.message}
          </p>
        ) : health.data ? (
          <>
            <p>
              Salud: <HealthBadge health={health.data.health} />
            </p>
            <dl className={styles.metrics}>
              {health.data.usage.map((metric) => (
                <div key={metric.key}>
                  <dt>{metric.label}</dt>
                  <dd>
                    {metric.value === null ? (
                      <span className={styles.unavailable} title={metric.unavailableReason ?? undefined}>
                        No disponible
                        <span className={styles.reason}>{metric.unavailableReason}</span>
                      </span>
                    ) : (
                      metric.value.toLocaleString('es-CO')
                    )}
                  </dd>
                </div>
              ))}
            </dl>
            <p className={styles.definition}>{HEALTH_DEFINITION}</p>
          </>
        ) : null}
      </Block>

      <Block title="Módulos habilitados" idPrefix={idPrefix} name="modules">
        <ul className={styles.chips}>
          {(tenant.enabledModules ?? []).map((key) => (
            <li key={key} className={styles.chip}>
              {moduleNames.get(key) ?? key}
            </li>
          ))}
        </ul>
        <button type="button" className={styles.linkBtn} onClick={() => onGoToTab('modules')}>
          Ver módulos
        </button>
      </Block>

      {canBilling ? (
        <Block title="Suscripción" idPrefix={idPrefix} name="subscription">
          {subscription.isLoading ? (
            <p className={styles.muted} role="status">Cargando suscripción…</p>
          ) : subscription.isError ? (
            <p className={styles.error} role="alert">
              No se pudo cargar la suscripción: {subscription.error.message}
            </p>
          ) : subscription.data ? (
            <>
              <p>
                {subscription.data.mrrKind === 'potential'
                  ? `MRR potencial: ${formatMoney(subscription.data.potentialMrr, subscription.data.currency)} al mes.`
                  : subscription.data.mrrKind === 'real'
                    ? `MRR: ${formatMoney(subscription.data.mrr, subscription.data.currency)} al mes.`
                    : `Contratado: ${formatMoney(subscription.data.contractedMrr, subscription.data.currency)} al mes; ${statusLabel(tenant.planStatus).toLowerCase()}, no suma al MRR.`}
              </p>
              <p className={styles.muted}>
                {subscription.data.items.length === 0
                  ? 'Ningún módulo en la suscripción.'
                  : `En la suscripción: ${subscription.data.items.map((item) => item.moduleName).join(', ')}.`}
              </p>
            </>
          ) : null}
          <button type="button" className={styles.linkBtn} onClick={() => onGoToTab('billing')}>
            Ver facturación
          </button>
        </Block>
      ) : null}

      {canAudit ? (
        <Block title="Línea de tiempo" idPrefix={idPrefix} name="timeline">
          {timeline.isLoading ? (
            <p className={styles.muted} role="status">Cargando…</p>
          ) : timeline.isError ? (
            <p className={styles.error} role="alert">
              No se pudo cargar la línea de tiempo: {timeline.error.message}
            </p>
          ) : (timeline.data?.entries ?? []).length === 0 ? (
            <p className={styles.muted}>Sin movimientos visibles para tu rol.</p>
          ) : (
            <ol className={styles.timeline}>
              {(timeline.data?.entries ?? []).map((entry) => (
                <li key={entry.id}>
                  <time dateTime={entry.createdAt}>{formatDateTime(entry.createdAt)}</time>{' '}
                  {entry.actionLabel}
                  {entry.outcome === 'failure' ? <span className={styles.failure}> (fallido)</span> : null}
                  {entry.actorEmail ? <span className={styles.muted}> · {entry.actorEmail}</span> : null}
                </li>
              ))}
            </ol>
          )}
          <button type="button" className={styles.linkBtn} onClick={() => onGoToTab('audit')}>
            Ver toda la auditoría
          </button>
        </Block>
      ) : null}
    </div>
  );
}
